// src/hooks/product/useProductColors.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useAlerts from "../useAlerts";
import useModals from "../useModals";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

import {
  loadingProductColors,
  loadingProductColorsDropdown,
  toggleStateProductColor,
  addProductColor,
  updateProductColor,
  updateFormField,
  setForm,
  resetForm,
  setFormErrors,
  clearFormErrors,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
} from "../../store/slices/product/productColorSlice";

import { IProductColorFormValues } from "../../types/product/productColorInterfaces";
import ProductColorService from "../../services/product/ProductColorService";

export const useProductColors = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  // ===============================
  // GLOBAL STATE
  // ===============================
  const {
    productColors,
    productColorsDropdown,
    form,
    isSubmit,
    formErrors,
    isCreateModalOpen,
    isEditModalOpen,
  } = useSelector((state: RootState) => state.productColors);

  // =========================================================
  // GET ALL
  // =========================================================
  const fetchProductColors = async () => {
    try {
      showLoadingIndicator();
      const response = await ProductColorService.getAll();
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(loadingProductColors(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =========================================================
  // DROPDOWN
  // =========================================================
  const fetchProductColorsDropdown = async () => {
    try {
      const response = await ProductColorService.getDropdown();
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(loadingProductColorsDropdown(data || []));
      }
    } catch (error) {
      handle(error);
    }
  };

  // =========================================================
  // DROPDOWN SEARCH
  // =========================================================
  const searchProductColorsDropdown = async (query: string) => {
    try {
      const response = await ProductColorService.searchDropdown(query);
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(loadingProductColorsDropdown(data || []));
      }
    } catch (error) {
      handle(error);
    }
  };

  // =========================================================
  // SEARCH (tabla/admin)
  // =========================================================
  const handlerSearchColors = async (query: string) => {
    try {
      showLoadingIndicator();
      const response = await ProductColorService.search(query);
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(loadingProductColors(data || []));
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =========================================================
  // SAVE / UPDATE
  // =========================================================
  const handlerAddProductColor = async (data: IProductColorFormValues) => {
    try {
      if (data.id === 0) {
        // CREATE
        const { id, ...dto } = data;
        const response = await ProductColorService.save(dto);
        const { statusCode, type, title, message, data: newColor } = response;

        if (statusCode === 200) {
          dispatch(addProductColor(newColor));
          dispatch(resetForm());
          dispatch(closeCreateModal());
          openModal(type, { title, message });
        }
      } else {
        // UPDATE
        const { id, ...dto } = data;
        const response = await ProductColorService.update(id, dto);
        const { statusCode, type, title, message, data: updatedColor } = response;

        if (statusCode === 200) {
          dispatch(updateProductColor(updatedColor));
          dispatch(resetForm());
          dispatch(closeEditModal());
          openModal(type, { title, message });
        }
      }
    } catch (error: any) {
      handle(error);
    }
  };

  // =========================================================
  // LOAD COLOR BY ID AND OPEN EDIT MODAL
  // =========================================================
  const loadProductColorForEdit = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await ProductColorService.getById(id);
      const { statusCode, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setForm(data));
        dispatch(clearFormErrors());
        dispatch(openEditModal());
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =========================================================
  // TOGGLE STATUS
  // =========================================================
  const toggleProductColorStatus = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await ProductColorService.toggleStatus(id);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(toggleStateProductColor(data));
        showAlert(type, title, message);
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =========================================================
  // OPEN/CLOSE MODALS
  // =========================================================
  const openEditProductColor = () => {
    dispatch(openEditModal());
  };

  const closeEditProductColor = () => {
    dispatch(closeEditModal());
    dispatch(resetForm());
    dispatch(clearFormErrors());
  };

  const openCreateProductColor = () => {
    dispatch(resetForm());
    dispatch(clearFormErrors());
    dispatch(openCreateModal());
  };

  const closeCreateProductColor = () => {
    dispatch(closeCreateModal());
    dispatch(resetForm());
    dispatch(clearFormErrors());
  };

  // =========================================================
  // FORM
  // =========================================================
  const updateField = (field: string, value: any) => {
    dispatch(updateFormField({ field, value }));
  };

  const saveFormErrors = (errors: Record<string, string>) => {
    dispatch(setFormErrors(errors));
  };

  const clearErrors = () => {
    dispatch(clearFormErrors());
  };

  const resetFormState = () => {
    dispatch(resetForm());
    dispatch(clearFormErrors());
  };

  return {
    // state
    productColors,
    productColorsDropdown,
    form,
    isSubmit,
    formErrors,
    isCreateModalOpen,
    isEditModalOpen,

    // crud
    fetchProductColors,
    fetchProductColorsDropdown,
    searchProductColorsDropdown,
    handlerSearchColors,
    handlerAddProductColor,
    loadProductColorForEdit,
    toggleProductColorStatus,

    // ui
    openEditProductColor,
    closeEditProductColor,
    openCreateProductColor,
    closeCreateProductColor,

    // form
    updateField,
    saveFormErrors,
    clearErrors,
    resetFormState,
  };
};
